/**
 * `/v1/resource-types`, `/v1/resources`, `/v1/allocations` — lesingene og de ni skrivingene.
 *
 * ================================================================================================
 * HVER SKRIVING INVALIDERER HELE `qk.resources(orgId)`, OG DET ER MED VILJE.
 *
 * En tildeling er ikke bare en rad: den er et forhold til hver ANNEN tildeling på den samme
 * ressursen. Å flytte Bil 2 en time kan løse en konflikt, lage en ny, og endre tallet ved siden av
 * «Transport» i avdelingstreet — tre svar i tre cacher, fra én skriving.
 *
 * Et smalere sett nøkler ville vært raskere og ville før eller siden glemt ett av dem. Prefikset er
 * en håndfull spørringer på en side, og det er billigere enn en teller som er feil.
 *
 * ================================================================================================
 * TELLERNE ER `CACHE.reference`, LISTA ER `CACHE.list`.
 *
 * Avgjørelse 6A: tellerne i treet er FASTE for årgangen og følger ikke filtrene. Da kan de ligge i
 * det samme nivået årgangens avdelingstre alt ligger i — og en side som bytter avdeling åtte ganger
 * gjør åtte listekall, ikke seksten.
 */
import {
  MAX_PAGE_SIZE,
  allocationDriverListResponseSchema,
  allocationDtoSchema,
  allocationListResponseSchema,
  allocationSummarySchema,
  timelineResponseSchema,
  conflictListResponseSchema,
  resourceDepartmentSummarySchema,
  resourceDeletionSchema,
  resourceDtoSchema,
  resourceTypeDeletionSchema,
  type ResourceDeletion,
  type ResourceOpenDay,
  type ResourceTypeDeletion,
  resourceListResponseSchema,
  resourceTypeDtoSchema,
  resourceTypeListResponseSchema,
  type AllocationDriverListResponse,
  type AllocationSource,
  type AllocationDto,
  type AllocationListResponse,
  type AllocationSummary,
  type TimelineResponse,
  type ConflictListResponse,
  type CreateAllocationRequest,
  type CreateResourceRequest,
  type CreateResourceTypeRequest,
  type ResourceDepartmentSummary,
  type ResourceDto,
  type ResourceListResponse,
  type ResourceTypeDto,
  type ResourceTypeListResponse,
  type UpdateAllocationRequest,
  type UpdateResourceRequest,
  type UpdateResourceTypeRequest,
  createResourceShiftsResponseSchema,
  type CreateResourceShiftsRequest,
  type CreateResourceShiftsResponse,
} from '@tvitvi/shared';
import { queryOptions, useMutation, type UseMutationResult } from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

/** Hva lista er filtrert på. Alt som er med her er med i cache-nøkkelen. */
export interface ResourceFilters {
  departmentId?: string | undefined;
  resourceTypeId?: string | undefined;
  q?: string | undefined;
  includeArchived?: boolean | undefined;
}

// ------------------------------------------------------------------------------- ressurstyper

/**
 * @param includeArchived Innstillinger ber om dem, velgerne gjør ikke.
 *
 * DET ER EN EGEN CACHE-NØKKEL, og det måtte det bli: to lister som ikke svarer det samme kan ikke
 * dele én. Uten flagget viste Ressurstyper-fanen en type som forsvant i det noen arkiverte den —
 * den lovte «arkiverte står nederst med en vei tilbake» og hadde ingen vei tilbake. Funnet av
 * `edition-settings-tabs.spec.ts`, som arkiverer og henter frem igjen framfor å sjekke at knappen
 * finnes.
 */
export function resourceTypesQueryOptions(
  orgId: string,
  festivalId: string | null,
  includeArchived = false,
) {
  return queryOptions({
    queryKey: qk.resourceTypeList(orgId, festivalId, includeArchived),
    queryFn: ({ signal }): Promise<ResourceTypeListResponse> =>
      apiRequest({
        path: '/resource-types',
        query: {
          ...(festivalId === null ? {} : { festivalId }),
          ...(includeArchived ? { includeArchived: 'true' } : {}),
          sort: 'order',
          direction: 'asc',
          limit: MAX_PAGE_SIZE,
        },
        schema: resourceTypeListResponseSchema,
        signal,
      }),
    ...CACHE.reference,
  });
}

export function useCreateResourceType(
  orgId: string,
): UseMutationResult<ResourceTypeDto, unknown, CreateResourceTypeRequest> {
  return useMutation({
    mutationFn: (body: CreateResourceTypeRequest) =>
      apiRequest({
        path: '/resource-types',
        method: 'POST',
        body,
        schema: resourceTypeDtoSchema,
      }),
    meta: { invalidates: [qk.resources(orgId)] },
  });
}

export interface UpdateResourceTypeInput {
  resourceTypeId: string;
  body: UpdateResourceTypeRequest;
}

export function useUpdateResourceType(
  orgId: string,
): UseMutationResult<ResourceTypeDto, unknown, UpdateResourceTypeInput> {
  return useMutation({
    mutationFn: ({ resourceTypeId, body }: UpdateResourceTypeInput) =>
      apiRequest({
        path: `/resource-types/${resourceTypeId}`,
        method: 'PATCH',
        body,
        schema: resourceTypeDtoSchema,
      }),
    meta: { invalidates: [qk.resources(orgId)] },
  });
}

/**
 * «SLETT KLADD» (QA13 runde 2, #31b).
 *
 * API-et har alltid tillatt sletting av en TOM type, og hodet på `ResourceTypesSettings` forklarer
 * hvorfor det ikke fantes en knapp: en type som forsvinner tar betydningen av hver tildeling den
 * bærer med seg, så «rydd opp i lista» er feil gest for en type i bruk.
 *
 * En KLADD er det ene tilfellet der den innvendingen ikke gjelder. Den har aldri kunnet velges, så
 * den bærer ingenting, og `remove` i tjenesten nekter uansett om `resourceCount > 0`.
 */
/**
 * `withResources`: ressursene av typen og reservasjonene deres går med (QA 23.09 punkt 4). Postene
 * og vaktene blir stående uten fast sted — derfor skiftene også.
 */
export function useRemoveResourceType(
  orgId: string,
): UseMutationResult<
  ResourceTypeDeletion,
  unknown,
  { resourceTypeId: string; withResources: boolean }
> {
  return useMutation({
    mutationFn: ({ resourceTypeId, withResources }) =>
      apiRequest({
        path: `/resource-types/${resourceTypeId}`,
        method: 'DELETE',
        ...(withResources ? { query: { withResources: 'true' } } : {}),
        schema: resourceTypeDeletionSchema,
      }),
    meta: { invalidates: [qk.resources(orgId), qk.shifts(orgId), qk.economy(orgId)] },
  });
}

/** «Angre» (23. september): typen, ressursene og det de tok med seg. */
export function useRestoreResourceType(
  orgId: string,
): UseMutationResult<unknown, unknown, { resourceTypeId: string; receipt: ResourceTypeDeletion }> {
  return useMutation({
    mutationFn: ({ resourceTypeId, receipt }) =>
      apiRequest({
        path: `/resource-types/${resourceTypeId}/restore`,
        method: 'POST',
        body: receipt,
      }),
    meta: { invalidates: [qk.resources(orgId), qk.shifts(orgId), qk.economy(orgId)] },
  });
}

/** Ressursen og reservasjonene på den (QA 23.09 punkt 4). Se `useRemoveResourceType`. */
export function useRemoveResource(
  orgId: string,
): UseMutationResult<ResourceDeletion, unknown, { resourceId: string; editionId: string }> {
  return useMutation({
    mutationFn: ({ resourceId, editionId }) =>
      apiRequest({
        path: `/resources/${resourceId}`,
        method: 'DELETE',
        query: { editionId, withReservations: 'true' },
        schema: resourceDeletionSchema,
      }),
    meta: { invalidates: [qk.resources(orgId), qk.shifts(orgId), qk.economy(orgId)] },
  });
}

export function useRestoreResource(
  orgId: string,
): UseMutationResult<unknown, unknown, { editionId: string; receipt: ResourceDeletion }> {
  return useMutation({
    mutationFn: ({ editionId, receipt }) =>
      apiRequest({
        path: `/resources/${receipt.resourceId}/restore`,
        method: 'POST',
        query: { editionId },
        body: receipt,
      }),
    meta: { invalidates: [qk.resources(orgId), qk.shifts(orgId), qk.economy(orgId)] },
  });
}

// ----------------------------------------------------------------------------------- ressurser

export function resourcesQueryOptions(orgId: string, editionId: string, filters: ResourceFilters) {
  return queryOptions({
    queryKey: qk.resourceList(orgId, editionId, { ...filters }),
    queryFn: ({ signal }): Promise<ResourceListResponse> =>
      apiRequest({
        path: '/resources',
        query: {
          editionId,
          ...(filters.departmentId === undefined ? {} : { departmentId: filters.departmentId }),
          ...(filters.resourceTypeId === undefined
            ? {}
            : { resourceTypeId: filters.resourceTypeId }),
          ...(filters.q === undefined || filters.q === '' ? {} : { q: filters.q }),
          ...(filters.includeArchived === true ? { includeArchived: 'true' } : {}),
          sort: 'order',
          direction: 'asc',
          limit: MAX_PAGE_SIZE,
        },
        schema: resourceListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export interface CreateResourceInput {
  editionId: string;
  body: CreateResourceRequest;
}

export function useCreateResource(
  orgId: string,
): UseMutationResult<ResourceDto, unknown, CreateResourceInput> {
  return useMutation({
    mutationFn: ({ editionId, body }: CreateResourceInput) =>
      apiRequest({
        path: '/resources',
        method: 'POST',
        query: { editionId },
        body,
        schema: resourceDtoSchema,
      }),
    meta: { invalidates: [qk.resources(orgId), qk.shifts(orgId)] },
  });
}

export interface UpdateResourceInput {
  resourceId: string;
  editionId: string;
  body: UpdateResourceRequest;
}

export function useUpdateResource(
  orgId: string,
): UseMutationResult<ResourceDto, unknown, UpdateResourceInput> {
  return useMutation({
    mutationFn: ({ resourceId, editionId, body }: UpdateResourceInput) =>
      apiRequest({
        path: `/resources/${resourceId}`,
        method: 'PATCH',
        query: { editionId },
        body,
        schema: resourceDtoSchema,
      }),
    meta: { invalidates: [qk.resources(orgId), qk.shifts(orgId), qk.economy(orgId)] },
  });
}

export interface SetResourceOpenDaysInput {
  resourceId: string;
  editionId: string;
  days: ResourceOpenDay[];
}

/**
 * ÅPNINGSTID PER DAG (52, 0925): hele lista for årgangen. Vaktplanen leser åpningstida for vaktas
 * dag, så den gjøres ugyldig sammen med ressursene.
 */
export function useSetResourceOpenDays(
  orgId: string,
): UseMutationResult<ResourceDto, unknown, SetResourceOpenDaysInput> {
  return useMutation({
    mutationFn: ({ resourceId, editionId, days }: SetResourceOpenDaysInput) =>
      apiRequest({
        path: `/resources/${resourceId}/open-days`,
        method: 'PUT',
        query: { editionId },
        body: { days },
        schema: resourceDtoSchema,
      }),
    meta: { invalidates: [qk.resources(orgId), qk.shifts(orgId)] },
  });
}

export interface CreateResourceShiftsInput {
  resourceId: string;
  body: CreateResourceShiftsRequest;
}

/** «Lag vakter» fra stedets åpningstid (sak 200.2). */
export function useCreateResourceShifts(
  orgId: string,
): UseMutationResult<CreateResourceShiftsResponse, unknown, CreateResourceShiftsInput> {
  return useMutation({
    mutationFn: ({ resourceId, body }: CreateResourceShiftsInput) =>
      apiRequest({
        path: `/resources/${resourceId}/shifts`,
        method: 'POST',
        body,
        schema: createResourceShiftsResponseSchema,
      }),
    meta: { invalidates: [qk.shifts(orgId), qk.resources(orgId)] },
  });
}

// --------------------------------------------------------------------------------- tildelinger

export interface AllocationFilters {
  from?: string | undefined;
  to?: string | undefined;
  resourceId?: string | undefined;
  departmentId?: string | undefined;
  /**
   * KILDEN (rettelse A2). «Hvilke tildelinger hoerer til DENNE oppgaven» — lista oppgavepanelet
   * tegner «Loesne» ved siden av. Uten filteret maatte panelet hente aargangens tildelinger og
   * sile dem i nettleseren.
   */
  sourceType?: AllocationSource | undefined;
  sourceId?: string | undefined;
}

export function allocationsQueryOptions(
  orgId: string,
  editionId: string,
  filters: AllocationFilters,
) {
  return queryOptions({
    queryKey: qk.allocationList(orgId, editionId, { ...filters }),
    queryFn: ({ signal }): Promise<AllocationListResponse> =>
      apiRequest({
        path: '/allocations',
        query: {
          editionId,
          ...(filters.from === undefined ? {} : { from: filters.from }),
          ...(filters.to === undefined ? {} : { to: filters.to }),
          ...(filters.resourceId === undefined ? {} : { resourceId: filters.resourceId }),
          ...(filters.departmentId === undefined ? {} : { departmentId: filters.departmentId }),
          ...(filters.sourceType === undefined ? {} : { sourceType: filters.sourceType }),
          ...(filters.sourceId === undefined ? {} : { sourceId: filters.sourceId }),
          sort: 'starts',
          direction: 'asc',
          limit: MAX_PAGE_SIZE,
        },
        schema: allocationListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function useCreateAllocation(
  orgId: string,
): UseMutationResult<AllocationDto, unknown, CreateAllocationRequest> {
  return useMutation({
    mutationFn: (body: CreateAllocationRequest) =>
      apiRequest({ path: '/allocations', method: 'POST', body, schema: allocationDtoSchema }),
    meta: { invalidates: [qk.resources(orgId), qk.economy(orgId)] },
  });
}

export interface UpdateAllocationInput {
  allocationId: string;
  body: UpdateAllocationRequest;
}

export function useUpdateAllocation(
  orgId: string,
): UseMutationResult<AllocationDto, unknown, UpdateAllocationInput> {
  return useMutation({
    mutationFn: ({ allocationId, body }: UpdateAllocationInput) =>
      apiRequest({
        path: `/allocations/${allocationId}`,
        method: 'PATCH',
        body,
        schema: allocationDtoSchema,
      }),
    /* Konsertens «Ressurser» leser de samme tildelingene (bølge 2, agent E). */
    meta: { invalidates: [qk.resources(orgId), qk.economy(orgId), qk.orgConcerts(orgId)] },
  });
}

/** «Avklar» (§2). Fjerner ikke konflikten — merker hvem som så den og når. */
export function useAcknowledgeAllocation(
  orgId: string,
): UseMutationResult<AllocationDto, unknown, string> {
  return useMutation({
    mutationFn: (allocationId: string) =>
      apiRequest({
        path: `/allocations/${allocationId}/acknowledge`,
        method: 'POST',
        schema: allocationDtoSchema,
      }),
    meta: { invalidates: [qk.resources(orgId)] },
  });
}

export function useRemoveAllocation(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (allocationId: string) =>
      apiRequest({ path: `/allocations/${allocationId}`, method: 'DELETE' }),
    meta: { invalidates: [qk.resources(orgId), qk.economy(orgId)] },
  });
}

/** «Angre» på en slettet reservasjon (23. september). */
export function useRestoreAllocation(orgId: string): UseMutationResult<unknown, unknown, string> {
  return useMutation({
    mutationFn: (allocationId: string) =>
      apiRequest({ path: `/allocations/${allocationId}/restore`, method: 'POST' }),
    meta: { invalidates: [qk.resources(orgId), qk.economy(orgId)] },
  });
}

// ----------------------------------------------------------------------------------- konflikter

export interface ConflictWindow {
  from: string;
  to: string;
  departmentId?: string | undefined;
}

export function conflictsQueryOptions(orgId: string, editionId: string, window: ConflictWindow) {
  return queryOptions({
    queryKey: qk.allocationConflicts(orgId, editionId, { ...window }),
    queryFn: ({ signal }): Promise<ConflictListResponse> =>
      apiRequest({
        path: '/allocations/conflicts',
        query: {
          editionId,
          from: window.from,
          to: window.to,
          ...(window.departmentId === undefined ? {} : { departmentId: window.departmentId }),
        },
        schema: conflictListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * SJÅFØRVELGEREN: hvem er på skift i tidsrommet (avgjørelse 7A, trinn 2.5).
 *
 * `CACHE.list` og ikke `CACHE.reference`: svaret følger vinduet, og et vindu som flytter seg er et
 * annet spørsmål. Nøkkelen bærer derfor begge endene.
 */
export function allocationDriversQueryOptions(
  orgId: string,
  editionId: string,
  window: { from: string; to: string },
) {
  return queryOptions({
    queryKey: qk.allocationDrivers(orgId, editionId, { ...window }),
    queryFn: ({ signal }): Promise<AllocationDriverListResponse> =>
      apiRequest({
        path: '/allocations/drivers',
        query: { editionId, from: window.from, to: window.to },
        schema: allocationDriverListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * «LISTE TIL KJØKKENET» — summert fra personene på tildelingen (§7.3, trinn 4.3).
 *
 * `CACHE.detail` og ikke `CACHE.reference`: dette er ÉN tildelings svar, og det endrer seg i det
 * noen flyttes av bussen eller får skrevet «vegetar» på personkortet sitt. Lista hentes derfor på
 * nytt hver gang noen åpner den, framfor å stå og bli feil i en kjøkkendør.
 */
export function allocationSummaryQueryOptions(orgId: string, allocationId: string) {
  return queryOptions({
    queryKey: qk.allocationSummary(orgId, allocationId),
    queryFn: ({ signal }): Promise<AllocationSummary> =>
      apiRequest({
        path: `/allocations/${allocationId}/summary`,
        schema: allocationSummarySchema,
        signal,
      }),
    ...CACHE.detail,
  });
}

/**
 * TIDSLINJA — mennesker og ting i det samme vinduet (trinn 5.1).
 *
 * `CACHE.list` og ikke `CACHE.reference`: svaret følger vinduet, og et vindu som flytter seg er et
 * annet spørsmål. Nøkkelen bærer derfor begge endene — samme form sjåførlista har.
 */
export function timelineQueryOptions(
  orgId: string,
  editionId: string,
  window: { from: string; to: string },
) {
  return queryOptions({
    queryKey: qk.allocationTimeline(orgId, editionId, { ...window }),
    queryFn: ({ signal }): Promise<TimelineResponse> =>
      apiRequest({
        path: '/allocations/timeline',
        query: { editionId, from: window.from, to: window.to },
        schema: timelineResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/** Tellerne i avdelingstreet. Faste for årgangen — se filhodet. */
export function resourceDepartmentSummaryQueryOptions(orgId: string, editionId: string) {
  return queryOptions({
    queryKey: qk.resourceDepartmentSummary(orgId, editionId),
    queryFn: ({ signal }): Promise<ResourceDepartmentSummary> =>
      apiRequest({
        path: '/resource-departments/summary',
        query: { editionId },
        schema: resourceDepartmentSummarySchema,
        signal,
      }),
    ...CACHE.reference,
  });
}
